import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { useAppDownload } from '@/context/AppDownloadContext';
import ListenerProfile from './components/ListenerProfile';
import FavoriteRadios from './components/FavoriteRadios';
import FollowedRadios from './components/FollowedRadios';
import ListeningHistory from './components/ListeningHistory';
import SentMessages from './components/SentMessages';
import SavedEvents from './components/SavedEvents';
import NotificationsList from './components/NotificationsList';
import SavedLives from './components/SavedLives';
import RecommendationSection from '@/components/feature/RecommendationSection';
import { generateRecommendationsFromProfile } from '@/mocks/recommendations';
import { radioStations } from '@/mocks/radios';
import {
  listenerProfile,
  favoriteRadios,
  followedRadios,
  listeningHistory,
  sentMessages,
  savedEvents,
  notificationsList,
  savedLives,
} from '@/mocks/listenerCenter';

type Section = 'profile' | 'favorites' | 'following' | 'history' | 'messages' | 'events' | 'notifications' | 'savedLives';
type PageStatus = 'loading' | 'ready' | 'error';

const sidebarSections: { key: Section; label: string; icon: string; count?: number; isExternal?: boolean; externalPath?: string }[] = [
  { key: 'profile', label: 'Perfil', icon: 'ri-user-3-line' },
  { key: 'favorites', label: 'Favoritas', icon: 'ri-heart-line', count: favoriteRadios.length },
  { key: 'following', label: 'Siguiendo', icon: 'ri-user-follow-line', count: followedRadios.length },
  { key: 'history', label: 'Historial', icon: 'ri-history-line' },
  { key: 'messages', label: 'Mensajes', icon: 'ri-message-2-line', count: sentMessages.length },
  { key: 'events', label: 'Eventos', icon: 'ri-calendar-event-line', count: savedEvents.length },
  { key: 'savedLives', label: 'Lives', icon: 'ri-live-line', count: savedLives.length },
  { key: 'notifications', label: 'Notificaciones', icon: 'ri-notification-3-line', count: notificationsList.filter((n) => !n.isRead && !n.isArchived).length },
  { key: 'explore', label: 'Explorar Radios', icon: 'ri-compass-3-line', isExternal: true, externalPath: '/explorar' },
];

function SkeletonCard() {
  return (
    <div className="bg-background-50 rounded-xl p-4 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-background-200"></div>
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-background-200 rounded w-3/4"></div>
          <div className="h-3 bg-background-200 rounded w-1/2"></div>
        </div>
      </div>
    </div>
  );
}

function SkeletonProfile() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="bg-background-50 rounded-2xl p-6 md:p-8">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="w-24 h-24 md:w-28 md:h-28 rounded-2xl bg-background-200 shrink-0"></div>
          <div className="flex-1 space-y-3">
            <div className="h-6 bg-background-200 rounded w-48"></div>
            <div className="h-4 bg-background-200 rounded w-full"></div>
            <div className="h-4 bg-background-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-background-50 rounded-xl p-4 text-center space-y-3">
            <div className="w-10 h-10 mx-auto rounded-xl bg-background-200"></div>
            <div className="h-6 bg-background-200 rounded w-16 mx-auto"></div>
            <div className="h-3 bg-background-200 rounded w-20 mx-auto"></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-3 animate-pulse">
      <div className="h-5 bg-background-200 rounded w-40 mb-4"></div>
      {[...Array(5)].map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

export default function ListenerCenter() {
  const [activeSection, setActiveSection] = useState<Section>('profile');
  const [pageStatus, setPageStatus] = useState<PageStatus>('ready');

  const unreadNotifs = notificationsList.filter((n) => !n.isRead && !n.isArchived).length;

  const { openModal: openDownloadModal } = useAppDownload();

  const listenerRecommendations = useMemo(() => {
    const userCategories = [...new Set(followedRadios.map((r) => r.category))];
    const userCities = [...new Set(followedRadios.map((r) => {
      const station = radioStations.find((s) => s.id === r.stationId);
      return station?.city || '';
    }).filter(Boolean))];
    const userStates = [...new Set(followedRadios.map((r) => {
      const station = radioStations.find((s) => s.id === r.stationId);
      return station?.state || '';
    }).filter(Boolean))];
    const excludeIds = followedRadios.map((r) => r.stationId);

    return generateRecommendationsFromProfile(userCategories, userCities, userStates, excludeIds, 6);
  }, []);

  const simulateLoading = () => {
    setPageStatus('loading');
    setTimeout(() => setPageStatus('ready'), 2000);
  };

  const simulateError = () => {
    setPageStatus('error');
  };

  const handleRetry = () => {
    simulateLoading();
  };

  const renderSectionContent = () => {
    if (pageStatus === 'loading') {
      return activeSection === 'profile' ? <SkeletonProfile /> : <SkeletonList />;
    }

    if (pageStatus === 'error') {
      return (
        <div className="bg-background-50 rounded-2xl p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-rose-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-3xl text-rose-500"></i>
          </div>
          <h3 className="text-lg font-bold text-foreground-950 mb-2">Algo salió mal</h3>
          <p className="text-sm text-foreground-500 mb-6 max-w-md mx-auto">
            No pudimos cargar esta sección. Verificá tu conexión y volvé a intentarlo.
          </p>
          <button
            onClick={handleRetry}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            Reintentar
          </button>
        </div>
      );
    }

    switch (activeSection) {
      case 'profile':
        return <ListenerProfile profile={listenerProfile} />;
      case 'favorites':
        return <FavoriteRadios radios={favoriteRadios} />;
      case 'following':
        return <FollowedRadios radios={followedRadios} />;
      case 'history':
        return <ListeningHistory history={listeningHistory} />;
      case 'messages':
        return <SentMessages messages={sentMessages} />;
      case 'events':
        return <SavedEvents events={savedEvents} />;
      case 'savedLives':
        return <SavedLives lives={savedLives} />;
      case 'notifications':
        return <NotificationsList notifications={notificationsList} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background-100/50 flex flex-col">
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6 pb-10">
          {/* Page Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-xl bg-primary-500 flex items-center justify-center">
                  <i className="ri-user-3-line text-background-50 text-lg"></i>
                </div>
                <div>
                  <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Centro de Oyentes</h1>
                  <p className="text-xs text-foreground-500">Tu espacio personal en RadioFrecuenciaWeb</p>
                </div>
              </div>
              {/* Debug controls - for demo purposes */}
              <div className="hidden sm:flex items-center gap-2">
                <button
                  onClick={openDownloadModal}
                  className="px-3 py-1.5 bg-accent-500 hover:bg-accent-600 text-background-50 dark:text-foreground-950 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-download-2-line mr-1"></i>
                  Descargar App
                </button>
                <button
                  onClick={simulateLoading}
                  className="px-3 py-1.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-lg hover:bg-amber-200 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-loader-2-line mr-1"></i>Simular Loading
                </button>
                <button
                  onClick={simulateError}
                  className="px-3 py-1.5 bg-rose-100 text-rose-700 text-xs font-medium rounded-lg hover:bg-rose-200 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-error-warning-line mr-1"></i>Simular Error
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Mobile Section Tabs */}
            <div className="lg:hidden -mx-1 overflow-x-auto no-scrollbar">
              <div className="flex gap-1 px-1 pb-2 min-w-max">
                {sidebarSections.map((s) =>
                  s.isExternal ? (
                    <Link
                      key={s.key}
                      to={s.externalPath || '#'}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all cursor-pointer bg-background-50 text-foreground-600 hover:bg-background-200"
                    >
                      <i className={`${s.icon} text-sm`}></i>
                      {s.label}
                    </Link>
                  ) : (
                    <button
                      key={s.key}
                      onClick={() => { setActiveSection(s.key); setPageStatus('ready'); }}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all cursor-pointer ${
                        activeSection === s.key
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-50 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      <i className={`${s.icon} text-sm`}></i>
                      {s.label}
                      {s.count !== undefined && s.count > 0 && (
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                          activeSection === s.key ? 'bg-background-50/20 text-background-50' : 'bg-foreground-100 text-foreground-600'
                        }`}>
                          {s.count}
                        </span>
                      )}
                    </button>
                  )
                )}
              </div>
            </div>

            {/* Desktop Sidebar */}
            <aside className="hidden lg:block w-56 shrink-0">
              <nav className="sticky top-24 bg-background-50 rounded-2xl p-3 space-y-1">
                {sidebarSections.map((s) =>
                  s.isExternal ? (
                    <Link
                      key={s.key}
                      to={s.externalPath || '#'}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer text-foreground-600 hover:bg-background-100 hover:text-foreground-900"
                    >
                      <i className={`${s.icon} text-base`}></i>
                      <span className="flex-1 text-left">{s.label}</span>
                      <i className="ri-arrow-right-up-line text-xs text-foreground-400"></i>
                    </Link>
                  ) : (
                    <button
                      key={s.key}
                      onClick={() => { setActiveSection(s.key); setPageStatus('ready'); }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                        activeSection === s.key
                          ? 'bg-primary-500 text-background-50 shadow-sm'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                      }`}
                    >
                      <i className={`${s.icon} text-base`}></i>
                      <span className="flex-1 text-left">{s.label}</span>
                      {s.count !== undefined && s.count > 0 && (
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          activeSection === s.key
                            ? 'bg-background-50/20 text-background-50'
                            : 'bg-foreground-100 text-foreground-600'
                        }`}>
                          {s.count}
                        </span>
                      )}
                    </button>
                  )
                )}
              </nav>
            </aside>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              {renderSectionContent()}
            </div>
          </div>
        </div>

        {/* Recomendaciones basadas en tu perfil */}
        {listenerRecommendations.length > 0 && pageStatus === 'ready' && (
          <div className="bg-background-50 mt-10">
            <RecommendationSection
              recommendations={listenerRecommendations}
              title="También te puede gustar"
              subtitle={`Basado en las categorías, ciudades y estilos de las radios que sigues.`}
              compact
            />
          </div>
        )}
      </main>

      <Footer />

      {/* Spacer para mobile */}
      <div className="md:hidden h-20"></div>
    </div>
  );
}